import React, { useState } from "react";
import { motion } from "framer-motion";
import { Target, Info } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { Badge } from "../GraderUI";
import CompetitorRow, { getTotalCompetitorMentions } from "./CompetitorRow";
import CompetitorsModal from "./CompetitorsModal";
import ScoreGauge from "./ScoreGauge";

const DONUT_COLORS = {
	won: "#10b981",
	lost: "#ef4444",
};

const COMPETITORS_PREVIEW = 10;

const SOV_TOOLTIP_TEXT = (
	<>
		<strong>Share of Voice</strong> = your brand mentions ÷ (your mentions + competitor
		mentions) across the AI prompts we tested. E.g.{" "}
		<em>&quot;8 brand vs 34 competitor mentions&quot;</em> means your brand appeared 8 times
		while competitors appeared 34 times — a 19% share for that engine.
	</>
);

function formatProviderLabel(providerKey) {
	const key = String(providerKey || "");
	if (!key) return "";
	return key.charAt(0).toUpperCase() + key.slice(1).toLowerCase();
}

function buildProviderSummary(provider, label, sov, brandMentions, competitorMentions) {
	if (provider.summary) return provider.summary;
	const key = String(provider.provider || label).toUpperCase();
	return `${key}: ${sov}/100 share of voice (${brandMentions} brand vs ${competitorMentions} competitor mentions).`;
}

function ProviderSovCard({ provider, index }) {
	const sov = Math.round(Number(provider.share_of_voice_score ?? 0));
	const brandMentions = Number(provider.brand_mentions ?? 0);
	const competitorMentions = Number(provider.competitor_mentions ?? 0);
	const label = formatProviderLabel(provider.provider);
	const summary = buildProviderSummary(provider, label, sov, brandMentions, competitorMentions);

	return (
		<motion.div
			className="market-competition__provider-card"
			initial={{ opacity: 0, y: 12 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.35, delay: index * 0.08 }}
		>
			<h4 className="market-competition__provider-name">{label}</h4>

			<ScoreGauge value={sov} label="Share of Voice" index={index} />

			<p className="market-competition__mentions">
				{brandMentions} brand vs {competitorMentions} competitor mentions
			</p>

			<p className="market-competition__provider-summary">{summary}</p>
		</motion.div>
	);
}

function PromptOutcomesDonut({ winning, losing }) {
	const total = winning + losing;
	if (total === 0) {
		return (
			<div className="market-competition__donut-empty">
				<p className="market-competition__donut-empty-text">No prompt data</p>
			</div>
		);
	}

	const wonPct = Math.round((winning / total) * 100);
	const data =
		losing > 0
			? [
					{ name: "Won", value: winning, color: DONUT_COLORS.won },
					{ name: "Lost", value: losing, color: DONUT_COLORS.lost },
				]
			: [{ name: "Won", value: winning, color: DONUT_COLORS.won }];

	return (
		<div className="market-competition__outcomes-row">
			<div className="market-competition__donut-wrap">
				<ResponsiveContainer width={160} height={160}>
					<PieChart>
						<Pie
							data={data}
							cx="50%"
							cy="50%"
							innerRadius={50}
							outerRadius={70}
							paddingAngle={losing > 0 ? 2 : 0}
							dataKey="value"
							stroke="none"
						>
							{data.map((entry) => (
								<Cell key={entry.name} fill={entry.color} />
							))}
						</Pie>
					</PieChart>
				</ResponsiveContainer>
				<div className="market-competition__donut-center">
					<span className="market-competition__donut-pct">{wonPct}%</span>
					<span className="market-competition__donut-label">won</span>
				</div>
			</div>
			<div className="market-competition__donut-legend">
				<span className="market-competition__legend-item">
					<span
						className="market-competition__legend-dot"
						style={{ background: DONUT_COLORS.won }}
					/>
					<strong>{winning} won</strong> — your brand appears
				</span>
				<span className="market-competition__legend-item">
					<span
						className="market-competition__legend-dot"
						style={{ background: DONUT_COLORS.lost }}
					/>
					<strong>{losing} lost</strong> — only competitors appear
				</span>
			</div>
		</div>
	);
}

export default function MarketCompetitionSection({ report }) {
	const [competitorsModalOpen, setCompetitorsModalOpen] = useState(false);
	const [showSovTooltip, setShowSovTooltip] = useState(false);

	if (!report?.providers?.length) return null;

	const competitors = report.competitors || [];
	const totalCompetitorMentions = getTotalCompetitorMentions(competitors);
	const previewCompetitors = competitors.slice(0, COMPETITORS_PREVIEW);
	const winning = Number(report.winning_prompts ?? 0);
	const losing = Number(report.losing_prompts ?? 0);

	return (
		<motion.div
			className="report-section market-competition"
			initial={{ opacity: 0, y: 20 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.5 }}
		>
			<div className="report-section__header market-competition__header">
				<div className="report-section__icon-wrap report-section__icon-wrap--purple">
					<Target className="report-section__icon" />
				</div>
				<div className="report-section__header-text">
					<div className="market-competition__title-row">
						<h2 className="report-section__title market-competition__title">
							Market Competition Analysis
						</h2>
						{report.is_measured && (
							<Badge variant="success" className="market-competition__measured-badge">
								Measured
							</Badge>
						)}
						<span
							className="market-competition__title-icon-wrap"
							onMouseEnter={() => setShowSovTooltip(true)}
							onMouseLeave={() => setShowSovTooltip(false)}
						>
							<Info
								className="market-competition__title-icon"
								aria-label="Share of Voice info"
							/>
							{showSovTooltip && (
								<div className="market-competition__sov-tooltip" role="tooltip">
									{SOV_TOOLTIP_TEXT}
								</div>
							)}
						</span>
					</div>
					<p className="report-section__subtitle">
						Real Share of Voice — your brand vs competitors named by AI across tested
						prompts
					</p>
				</div>
			</div>

			<div className="market-competition__provider-grid">
				{report.providers.map((provider, index) => (
					<ProviderSovCard key={provider.provider} provider={provider} index={index} />
				))}
			</div>

			{(competitors.length > 0 || winning + losing > 0) && (
				<div className="market-competition__bottom">
					{competitors.length > 0 && (
						<div className="market-competition__competitors">
							<h3 className="market-competition__section-title">
								Top competitors AI names ({competitors.length})
							</h3>
							<ul className="market-competition__competitor-list">
								{previewCompetitors.map((comp, i) => (
									<CompetitorRow
										key={comp.name || i}
										competitor={comp}
										totalMentions={totalCompetitorMentions}
									/>
								))}
							</ul>
							{competitors.length > COMPETITORS_PREVIEW && (
								<button
									type="button"
									className="market-competition__show-all"
									onClick={() => setCompetitorsModalOpen(true)}
								>
									Show all {competitors.length} competitors
								</button>
							)}
						</div>
					)}

					<div className="market-competition__outcomes">
						<h3 className="market-competition__section-title">Prompt outcomes</h3>
						<PromptOutcomesDonut winning={winning} losing={losing} />
					</div>
				</div>
			)}

			{report.consensus_summary && (
				<div className="report-section__callout report-section__callout--purple market-competition__footer">
					<p className="report-section__callout-text">{report.consensus_summary}</p>
				</div>
			)}

			{competitorsModalOpen && (
				<CompetitorsModal
					competitors={competitors}
					onClose={() => setCompetitorsModalOpen(false)}
				/>
			)}
		</motion.div>
	);
}
